/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import {FilterSearch} from '../../filters/FilterSearch';
import {useIsPhone} from '../../hooks/useMedia';
import Render from '../../render/Render';
import {dlsColor, dlsZIndex} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';

export interface GlobalGroupChooserSearchProps {
  setSearchValue: Function;
  setRenderingData: Function;
  clearSearch?: boolean;
  listLength?: number;
  inputLength?: number;
  onlyAccountId?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  globalGroupSearch : {
    padding                        : '12px 0',
    [theme.breakpoints.down('sm')] : {
      padding : ' 6px 0'
    }
  },
  inputOverlay : {
    position       : 'absolute',
    right          : '100px',
    color          : dlsColor('grey4'),
    fontSize       : '.8rem',
    height         : '40px',
    zIndex         : dlsZIndex('globalGroupChooserSearchInputOverlay'),
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'center'
  }
}) as any);

/**
 * Abstraction of the Filter Search component to fit the global group
 * search for desktop and mobile
 *
 * ```typescript
 * import {GlobalGroupChooserSearch} from '@ion/dls/src/components/groupchooser';
 *
 *   <GlobalGroupChooserSearch setSearchValue={setSearchValue}
 *     clearSearch={false} />
 * ```
 */
export const GlobalGroupChooserSearch = ({
  setSearchValue,
  clearSearch = false,
  listLength = 0,
  inputLength = 3,
  setRenderingData,
  onlyAccountId = false
}: GlobalGroupChooserSearchProps) => {
  const classes                         = useStyles();
  const [valueLength, setValueLength]   = useState(0);
  const [showOverlay, setShowOverlay]   = useState('');
  const [disableInput, setDisableInput] = useState(false);
  const isPhone                         = useIsPhone();
  const latestRef                       = useRef({
    valueLength : 0, listLength, inputLength, onlyAccountId
  });
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    isLegacy : isLegacyTheme
  } = useThemeContext();

  useEffect(() => {
    latestRef.current = {valueLength, listLength, inputLength, onlyAccountId};
  }, [valueLength, listLength, inputLength, onlyAccountId]);

  const debounceSearchRef = useRef<ReturnType<typeof debounce> | null>(null);
  if (!debounceSearchRef.current) {
    debounceSearchRef.current = debounce((value: string) => {
      const {valueLength, listLength, inputLength, onlyAccountId} = latestRef.current;
      const longList           = listLength >= 75;
      const valLength          = value.length;
      const shortInput         = value.length < inputLength;
      const shortExistingValue = valueLength < inputLength;
      if (!shortInput|| !longList) {
        // list is either short enough or the
        setShowOverlay('');
        setSearchValue(value);
        GlobalGroupsSearch.findResults(value, onlyAccountId);
      } else if (valLength && shortInput && shortExistingValue && longList) {
        // user has started typing but the search string is too short
        setShowOverlay(translate('common:search.keepTyping'));
      } else if (shortInput && !shortExistingValue && longList) {
        // user has deleted the search value to a point where it's too short
        // to perform a search and we need to reload the list, which takes time
        loadingData();
        GlobalGroupsSearch.findResults('');
      } else if (!valLength) {
        // make sure we don't show any overlays when there is no search value
        setShowOverlay('');
        GlobalGroupsSearch.findResults('');
      }
      setValueLength(valLength);
    }, 250);
  }

  function loadingData() {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    setShowOverlay(translate('common:states.oneMoment'));
    setRenderingData(true);
    setSearchValue('');
    loadingTimeoutRef.current = setTimeout(() => {
      setRenderingData(false);
      setShowOverlay(translate('common:search.keepTyping'));
    }, 250);
  }

  function onChange(value: string) {
    debounceSearchRef.current?.(value);
  }

  useEffect(() => {
    return () => {
      // drop any queued search so it can't set state or call findResults after unmount
      debounceSearchRef.current?.cancel();
      // clear a pending loadingData timer so it can't toggle the rendering state after unmount
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={isLegacyTheme ? classes.globalGroupSearch : ''}>
      <Render.If condition={showOverlay && isLegacyTheme}>
        <span className={classes.inputOverlay}>
          {showOverlay}
        </span>
      </Render.If>
      <FilterSearch clearSearch={clearSearch}
        suppressAutoFocusOnOpen={isPhone}
        onChange={onChange}
        disabled={disableInput}
        openFilterSearch
        fullWidth />
    </div>
  );
};

export default GlobalGroupChooserSearch;
